import React from 'react';
import { ElementSize } from '../../types/element-size';
import { ButtonType } from '../../types/button-type';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';

interface ButtonProps<T extends React.ElementType = 'button'> {
  as?: T;
  className?: string;
  style?: React.CSSProperties;
  size?: ElementSize;
  type?: ButtonType;
  text?: string;
  submit?: boolean;
  href?: string;
  children?: string | number | React.ReactNode;
  onClick?: (event?: React.MouseEvent) => void;
  disabled?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  customType?: string;
  isLoading?: boolean;
  loadingElement?: React.ReactNode;
  isAdaptive?: boolean;
}

const SIZE_CLASSES = {
  XL: 'text-lg font-bold min-h-11 min-w-36',
  LG: 'text-base min-h-10 min-w-[124px]',
  MD: 'text-sm min-h-9 min-w-20 px-4',
  SM: 'text-xs min-h-7 min-w-16 px-2.5',
  XS: 'text-xs min-h-6 min-w-14 px-2',
};

const TYPE_BUTTON_CLASSES = {
  primary: 'disabled:bg-zinc-400 disabled:border-zinc-600 disabled:text-zinc-600 bg-primary text-white font-medium border border-burnt-orang hover:bg-[#ff9146] active:bg-[ff6700]',
  'primary-inverse': 'disabled:text-zinc-600 disabled:border-black bg-transparent text-black font-medium border border-burnt-orang hover:border-orange-500 hover:text-orange-500 active:border-orange-500 active:text-orange-200 dark:text-white hover:dark:border-orange-500 hover:dark:text-orange-500 active:dark:border-orange-500 active:dark:text-orange-200',
  'primary-inverse-white': 'disabled:bg-zinc-400 disabled:border-zinc-600 disabled:text-zinc-600 text-orange-500 hover:text-white font-medium border border-burnt-orang hover:bg-[#ff9146] active:bg-[ff6700]',
  transparent: 'disabled:text-zinc-400 bg-transparent text-black font-medium border-none hover:underline dark:text-white',
};

const TYPE_SPINNER_CLASSES = {
  primary: 'border-white',
  secondary: 'border-primary',
};

const ADAPTIVE_STYLE = 'xs:text-xs xs:min-h-6 xs:min-w-14 xs:px-2 sm:text-xs sm:min-h-7 sm:min-w-16 sm:px-2.5 md:text-sm md:min-h-9 md:min-w-20 md:px-4 lg:text-base lg:min-h-10 lg:min-w-[124px] xl:text-lg xl:font-bold xl:min-h-11 xl:min-w-3';

const InteractiveButton = <T extends React.ElementType = 'button'>({
  as,
  className = '',
  style,
  size = ElementSize.MD,
  type,
  text,
  submit,
  href,
  onClick,
  disabled,
  iconLeft,
  iconRight,
  customType,
  isLoading,
  loadingElement,
  children,
  isAdaptive,
  ...rest
}: ButtonProps<T> & Omit<React.ComponentPropsWithoutRef<T>, keyof ButtonProps>) => {
  const Tag = as || 'button';

  const personalizedType = (customType && customType.length > 0) ? customType : null;

  const getSize = (size: ElementSize, isAdaptive: boolean | undefined) => {
    return isAdaptive ? ADAPTIVE_STYLE : SIZE_CLASSES[size];
  }

  const classSize = getSize(size, isAdaptive);
  const classButtonType = personalizedType || TYPE_BUTTON_CLASSES[type] || TYPE_BUTTON_CLASSES['primary'];
  const classNameButton = `flex justify-center items-center rounded transition-all duration-200 ${classSize} ${className} ${classButtonType}`;
  const classSpinnerType = TYPE_SPINNER_CLASSES[type] || TYPE_SPINNER_CLASSES['primary'];
  const classNameSpinner = `h-5 w-5 ${classSpinnerType}`;

  const getLengthElements = () => {
    let length = 0;

    if (iconLeft) {
      length++;
    }

    if (iconRight) {
      length++;
    }

    if (text) {
      length++;
    }

    if (children) {
      length++;
    }

    return length;
  };
  const lengthElement = getLengthElements();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (typeof onClick === 'function') {
      onClick(event);
    }
  };

  return (
    <Tag
      {...(Tag === 'a' ? { href } : {})}
      {...(Tag === 'button'
        ? { type: submit ? 'submit' : 'button', disabled }
        : {})}
      style={style}
      className={classNameButton}
      onClick={handleClick}
      {...rest}
    >
      {isLoading ? (loadingElement || <SpinnerLoader className={classNameSpinner} />) : (
        <div className={`c-interactive-button__elements flex flex-row justify-center items-center ${lengthElement > 1 ? 'gap-x-2' : ''}`}>
          {iconLeft && <span className="c-interactive-button__icon flex">{iconLeft}</span>}
          <span>{text || children}</span>
          {iconRight && <span className="c-interactive-button__icon flex">{iconRight}</span>}
        </div>
      )}
    </Tag>
  );
};

export default InteractiveButton;